import { CheckCircle, Target, Camera, Scissors, MessageCircle } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const WHATSAPP_NUMBER = '5575981872440';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, vim do site de nódulo e gostaria de agendar uma consulta com dr. Mateus')}`;

const biopsyOptions = [
  {
    icon: Target,
    title: 'Biópsia Percutânea',
    subtitle: 'Orientada por Tomografia',
    color: '#0D47A1',
    image: '/images/biopsia_percutanea.jpg',
    when: [
      'Lesões periféricas com margem de segurança para estruturas vasculares',
    ],
    how: 'Posicionamento de uma agulha fina dentro da lesão com o auxílio da Tomografia Computadorizada, seguida de retirada de fragmentos para análise laboratorial (anatomia patológica, culturas quando necessário). Pode ser realizada com anestesia local ou sedação.',
    advantages: [
      'Minimamente invasivo',
      'Alta no mesmo dia',
      'Baixa taxa de complicações',
      'Retorno ao trabalho: 24–48h',
    ],
  },
  {
    icon: Camera,
    title: 'Broncoscopia',
    subtitle: 'com Biópsia',
    color: '#00897B',
    image: '/images/broncoscopia.jpg',
    when: [
      'Lesões centrais com acometimento de via aérea',
    ],
    how: 'De forma parecida com uma Endoscopia Digestiva, é realizada a introdução de um aparelho com uma câmera na extremidade para avaliação e biópsia da lesão. Realizada com sedação ou anestesia geral.',
    advantages: [
      'Minimamente invasivo — sem cortes',
      'Alta no mesmo dia',
      'Baixa taxa de complicações',
      'Retorno ao trabalho: 24–48h',
    ],
  },
  {
    icon: Scissors,
    title: 'Ressecção Cirúrgica',
    subtitle: 'por VATS (Vídeotoracoscopia)',
    color: '#0D47A1',
    image: '/images/resseccao_cirurgica.jpg',
    when: [
      'Quando o paciente pode se beneficiar da retirada da lesão mesmo antes do diagnóstico inicial',
      'Na impossibilidade de biópsia por outros métodos',
    ],
    how: 'Retirada da lesão por cirurgia minimamente invasiva (VATS — vídeo-assistida). Pequenas incisões. Câmera e instrumentos permitem visão interna. Remove o segmento ou lobo pulmonar contendo o nódulo. Realizada com anestesia geral.',
    advantages: [
      'Minimamente invasivo (cirurgia por vídeo)',
      'Diagnóstico e tratamento em um único procedimento',
      'Recuperação rápida comparada à cirurgia aberta',
    ],
  },
];

export default function NoduloBiopsySection() {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      id="biopsia"
      className={`py-24 md:py-32 bg-[#EEF4FF] transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#0D47A1]/10 text-[#0D47A1] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Procedimentos Diagnósticos
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            Opções de Procedimentos<br />Diagnósticos para Nódulo Pulmonar
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            Dependendo das características do seu nódulo, existem diferentes abordagens.
            Aqui estão as principais, explicadas de forma clara:
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {biopsyOptions.map((option, i) => {
            const Icon = option.icon;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                {/* Portrait image — aspect 3:4 */}
                <div className="relative w-full overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={option.image}
                    alt={option.title}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D47A1]/75 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                      >
                        <Icon size={18} className="text-white" />
                      </div>
                      <div>
                        <div className="font-poppins font-bold text-white text-base leading-tight">{option.title}</div>
                        <div className="text-white/80 text-xs">{option.subtitle}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-[#212121]/50 uppercase tracking-wide mb-2">Quando está indicada</p>
                    <ul className="space-y-1">
                      {option.when.map((w, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span
                            className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                            style={{ backgroundColor: option.color }}
                          />
                          <span className="text-[#212121]/70 text-sm">{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-semibold text-[#212121]/50 uppercase tracking-wide mb-2">Como funciona</p>
                    <p className="text-[#212121]/65 text-sm leading-relaxed">{option.how}</p>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-[#212121]/50 uppercase tracking-wide mb-2">Vantagens</p>
                    <ul className="space-y-1.5">
                      {option.advantages.map((adv, j) => (
                        <li key={j} className="flex items-center gap-2">
                          <CheckCircle size={14} style={{ color: option.color }} className="flex-shrink-0" />
                          <span className="text-[#212121]/70 text-sm">{adv}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <p className="text-[#212121]/60 text-sm mb-4">Qual opção é ideal para o seu caso? Agende uma avaliação.</p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5"
          >
            <MessageCircle size={18} />
            QUAL OPÇÃO É IDEAL PARA MEU CASO?
          </a>
        </div>

      </div>
    </section>
  );
}
